import { useState } from 'react'

import classNames from 'classnames'

import BlockContent from '~/components/atoms/BlockContent/BlockContent'
import Container from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import { Icon } from '~/components/atoms/Icon/Icon'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import type { ComponentTheme } from '~/components/themes'
import type { Block } from '~/services/sanity'

import styles from './CalloutWithChecklist.module.css'

interface CalloutWithChecklistProps {
  title: string
  summary?: Block[]
  checklistItems: {
    title: string
    summary: string
    defaultChecked?: boolean
  }[]

  noPaddingBottom?: boolean
  theme?: ComponentTheme
  verticalPadding?: 'none' | 'small' | 'medium' | 'large'
}

const CalloutWithChecklist = ({
  title,
  summary,
  checklistItems,
  theme,
  verticalPadding,
  noPaddingBottom = false,
}: CalloutWithChecklistProps) => {
  const [checkedItems, setCheckedItems] = useState<boolean[]>(
    checklistItems.map(item => item.defaultChecked || false)
  )

  const handleCheckboxChange = (index: number) => {
    const newCheckedItems = [...checkedItems]
    newCheckedItems[index] = !newCheckedItems[index]
    setCheckedItems(newCheckedItems)
  }

  return (
    <Fixture
      verticalPadding={verticalPadding !== 'none'}
      noPaddingBottom={noPaddingBottom}
      theme={theme}
    >
      <div className={styles.base}>
        <Container>
          <div className={styles.container}>
            <div className={styles.contentContainer}>
              <div className={styles.content}>
                <Text className={styles.title} size={TextSizes.primaryTitleMed}>
                  {title}
                </Text>
                {summary && (
                  <div className={classNames(styles.summary)}>
                    <BlockContent value={summary} />
                  </div>
                )}
              </div>
            </div>
            <div className={styles.checklistContainer}>
              <div className={classNames(styles.checklist)}>
                {checklistItems.map((item, index) => (
                  <label
                    key={index}
                    className={classNames(styles.checklistItem)}
                  >
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      checked={checkedItems[index]}
                      onChange={() => handleCheckboxChange(index)}
                    />
                    <div className={styles.checkboxBox}>
                      {checkedItems[index] && (
                        <Icon type="PortfolioAddedTick" />
                      )}
                    </div>
                    <div className={styles.checkboxContent}>
                      <Text
                        className={styles.checklistItemTitle}
                        size={TextSizes.primaryTitleMed}
                      >
                        {item.title}
                      </Text>
                      <Text
                        className={styles.checklistItemSummary}
                        size={TextSizes.bodyMed}
                      >
                        {item.summary}
                      </Text>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </div>
    </Fixture>
  )
}

export default CalloutWithChecklist
